<template>
  <div>
    <div :class="containerClass">
      <page-header
        :title="title"
        :container-class="null"
        :info-text="'LMS, assessment and assignment reporting for your school.'"
        :show-back="true"
        class="mb-24pt"
      />
    </div>

    <div :class="containerClass">
      <!-- Buckets, not links: each one swaps the report shown below it. -->
      <nav class="buckets" aria-label="Reporting sections">
        <router-link
          v-for="bucket in buckets"
          :key="bucket.name"
          :to="{ name: bucket.name }"
          class="bucket"
          active-class="bucket--active"
          exact
        >
          <span class="bucket__icon" v-html="bucket.icon"></span>
          <span class="bucket__label">{{ bucket.label }}</span>
        </router-link>
      </nav>
    </div>

    <router-view />
  </div>
</template>

<script>
import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';

// Inline SVG rather than the icon font: not all of these material glyphs are in
// the shipped subset, and the missing ones render as empty boxes.
const ICON_GENERAL = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M12 7.5C10.6 6.2 8.7 5.5 6.5 5.5H3v12h3.5c2.2 0 4.1.7 5.5 2" />
    <path d="M12 7.5c1.4-1.3 3.3-2 5.5-2H21v12h-3.5c-2.2 0-4.1.7-5.5 2" />
    <path d="M12 7.5v14" />
  </svg>`;

const ICON_ASSESSMENTS = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <rect x="4" y="4" width="16" height="17" rx="2.5" />
    <path d="M9 4.5A1.5 1.5 0 0 1 10.5 3h3A1.5 1.5 0 0 1 15 4.5V6H9z" />
    <path d="M8.5 13l2.5 2.5 4.5-5" />
  </svg>`;

const ICON_ASSIGNMENTS = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <circle cx="10" cy="7.5" r="3.5" />
    <path d="M3.5 20c0-3.3 2.9-6 6.5-6 1.2 0 2.3.3 3.2.8" />
    <circle cx="17" cy="17" r="4" />
    <path d="M15.3 17l1.2 1.2 2.2-2.4" />
  </svg>`;

const BUCKETS = [
  { name: 'school-lms-analytics', label: 'General', icon: ICON_GENERAL },
  { name: 'school-lms-assessment-reporting', label: 'Assessments', icon: ICON_ASSESSMENTS },
  { name: 'school-lms-assignment-analytics', label: 'Assignments', icon: ICON_ASSIGNMENTS },
];

export default {
  components: { PageHeader },
  extends: Page,
  data() {
    return {
      title: 'Reporting',
      buckets: BUCKETS,
    };
  },
};
</script>

<style scoped lang="scss">
.buckets {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  margin-bottom: 28px;
}

.bucket {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 24px 16px;
  border: 1px solid #e5e9f0;
  border-radius: 14px;
  background: #fff;
  text-decoration: none;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;

  &:hover,
  &:focus-visible {
    border-color: #b9d2f8;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
    text-decoration: none;
  }
}

.bucket__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 62px;
  height: 62px;
  border-radius: 12px;
  background: #f3f5f9;
  color: #475569;
}

.bucket__icon ::v-deep svg {
  width: 30px;
  height: 30px;
}

.bucket__label {
  font-size: 1rem;
  font-weight: 700;
  color: #334155;
}

.bucket--active {
  border-color: #3b86f5;
  background: #eef4fe;

  .bucket__icon {
    background: #e0ebfd;
    color: #3b86f5;
  }

  .bucket__label {
    color: #3b86f5;
  }
}

@media (max-width: 767.98px) {
  .buckets {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .bucket {
    flex-direction: row;
    justify-content: flex-start;
    padding: 14px 16px;
  }

  .bucket__icon {
    width: 46px;
    height: 46px;
  }
}
</style>
